import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface CardRadioProps {
  name: string;
  value: string;
  selected: boolean;
  title: string;
  description?: string;
  icon?: ReactNode;
  onSelect: (value: string) => void;
  disabled?: boolean;
  className?: string;
  "data-test"?: string;
  /**
   * Roving-tabindex value (WAI-ARIA radiogroup pattern): exactly one radio
   * per group should be `0` (the selected one, or the first when none is
   * selected yet); every other sibling must be `-1` so `Tab` enters/exits
   * the group in one stop instead of stopping on every card. The caller
   * (owner of the full `role="radiogroup"`) computes this since a single
   * `CardRadio` can't see its siblings' selection state. Omitted, no
   * `tabindex` attribute is rendered — the native `<button>` default
   * applies (always a Tab stop) — so a lone/standalone `CardRadio` stays
   * reachable; multi-option groups should always pass this explicitly.
   */
  tabIndex?: 0 | -1;
}

const ARROW_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
]);

/**
 * CardRadio — a single option in a two/N-card picker (design spec §4.1.2,
 * §4.1.3). Renders as `<button role="radio">` so it is keyboard-activatable
 * (Tab + Space/Enter) without any manual key handling. Arrow-key roving
 * navigation between sibling radios within the enclosing `role="radiogroup"`
 * is implemented here (design spec §6.3: not required for two-option
 * pickers, but required for multi-option ones — implemented uniformly so
 * every picker gets it for free). Selected state uses the existing
 * card-picker tokens (`--brand-soft` fill, `--brand-line` border) — no new
 * colour.
 */
export function CardRadio({
  name,
  value,
  selected,
  title,
  description,
  icon,
  onSelect,
  disabled = false,
  className,
  "data-test": dataTest,
  tabIndex,
}: CardRadioProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!ARROW_KEYS.has(event.key)) {
      return;
    }
    const group = event.currentTarget.closest('[role="radiogroup"]');
    if (!group) {
      return;
    }
    const radios = Array.from(
      group.querySelectorAll<HTMLButtonElement>('[role="radio"]'),
    ).filter((el) => !el.disabled);
    const currentIndex = radios.indexOf(event.currentTarget);
    if (currentIndex === -1 || radios.length === 0) {
      return;
    }
    event.preventDefault();
    const direction = event.key === "ArrowLeft" || event.key === "ArrowUp"
      ? -1
      : 1;
    const nextIndex = (currentIndex + direction + radios.length) %
      radios.length;
    const next = radios[nextIndex];
    next.focus();
    next.click();
  };

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      name={name}
      disabled={disabled}
      tabIndex={tabIndex}
      onClick={() => onSelect(value)}
      onKeyDown={handleKeyDown}
      data-test={dataTest}
      className={cn(
        "flex w-full items-start gap-3 rounded-[var(--r-md)] border p-4 text-left transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        selected
          ? "border-[var(--brand-line)] bg-[var(--brand-soft)]"
          : "border-[var(--border)] bg-[var(--background)] hover:bg-[color:color-mix(in_srgb,var(--accent)_50%,transparent)]",
        className,
      )}
    >
      {icon && (
        <span
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-[var(--muted-foreground)]"
        >
          {icon}
        </span>
      )}
      <span className="flex-1">
        <span className="block font-medium text-sm text-[var(--foreground)]">
          {title}
        </span>
        {description && (
          <span className="mt-0.5 block text-sm text-[var(--muted-foreground)]">
            {description}
          </span>
        )}
      </span>
    </button>
  );
}
